import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import DuprPartnerBadge from "../components/DuprPartnerBadge.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/pickleball-tournament-software')({
    component: RouteComponent,
})

const POOL_BENEFITS = [
    {title: "Create pool play groups", desc: "Split teams or players into pools and generate round robin fixtures."},
    {title: "Advance qualifiers", desc: "Move top pool finishers into championship or medal brackets."},
    {title: "Run consolation brackets", desc: "Give players more matches even after they miss the main bracket."},
];

const DIVISIONS = [
    {label: "Skill divisions", title: "3.0, 3.5, 4.0 and open events", desc: "Create divisions for beginner, intermediate, advanced and open-level players."},
    {label: "Event types", title: "Singles, doubles and mixed doubles", desc: "Run separate draws for singles, doubles and mixed doubles categories."},
    {label: "Medal brackets", title: "Gold, silver and bronze matches", desc: "Track final placements and make medal matches easy to follow."},
    {label: "Consolation", title: "Keep players involved", desc: "Create extra match opportunities through consolation or placement brackets."},
    {label: "Courts", title: "Schedule across multiple courts", desc: "Keep divisions moving with court assignments and match progression."},
    {label: "Standings", title: "Pool and bracket visibility", desc: "Publish pool tables, bracket progress and final standings clearly."},
];

const FEATURES = [
    {code: "RG", title: "Online registration & signup", desc: "Let players sign up online with custom forms, skill-division selection, partner choice for doubles, waivers and entry-fee payments."},
    {code: "PL", title: "Pool play management", desc: "Create groups, generate matches and track pool standings."},
    {code: "BR", title: "Championship brackets", desc: "Move qualifiers into final brackets after pool play."},
    {code: "MD", title: "Medal matches", desc: "Organize gold, silver, bronze and placement matches."},
    {code: "DV", title: "Division setup", desc: "Separate events by skill level, age group, gender or category."},
    {code: "SC", title: "Live score tracking", desc: "Record scores and keep brackets and standings current."},
    {code: "CT", title: "Court scheduling", desc: "Manage matches across multiple courts and time slots."},
];

const FORMATS = [
    {name: "Round Robin", sub: "Pool play and group standings", to: "/round-robin-tournament-generator/"},
    {name: "Pool Play + Knockout", sub: "Groups into finals", to: "/group-and-knockout-generator/"},
    {name: "Single Elimination", sub: "Fast championship brackets", to: "/single-elimination-bracket-generator/"},
    {name: "Double Elimination", sub: "Second-chance brackets", to: "/double-elimination-bracket-generator/"},
    {name: "Ladder Events", sub: "Ongoing player rankings", to: "/ladder-league-generator/"},
    {name: "Swiss System", sub: "Large competitive events", to: "/swiss-system-tournament-generator/"},
    {name: "King of the Court", sub: "Fast court rotations", to: "/king-of-the-court-generator/"},
    {name: "Pickleball Leagues", sub: "Recurring fixtures and standings", to: "/pickleball-league-software/"},
];

const OPS_BENEFITS = [
    {title: "Keep divisions separate", desc: "Manage each skill level and event category without mixing results."},
    {title: "Move from pools to brackets", desc: "Use pool standings to seed championship and consolation brackets."},
    {title: "Improve player communication", desc: "Make it easier for players to follow courts, matchups and results."},
];

const COMPARE = [
    {need: "Pool play", manual: "Manual group tables and formulas", scorepal: "Organized pool fixtures and standings"},
    {need: "Skill divisions", manual: "Separate tabs and duplicated templates", scorepal: "Clear divisions and event categories"},
    {need: "Bracket seeding", manual: "Manual updates after pool play", scorepal: "Clear path from pools into brackets"},
    {need: "Medal matches", manual: "Easy to miss or miscommunicate", scorepal: "Gold, silver, bronze and placement matches stay visible"},
    {need: "Player experience", manual: "Players ask organizers for courts and updates", scorepal: "Fixtures, results and standings are easier to follow"},
];

const FAQS = [
    {
        q: 'What is pickleball tournament software?',
        a: <>Pickleball tournament software helps organizers manage pool play, brackets, divisions, scores, standings, medal matches and player communication.</>,
    },
    {
        q: 'Can players register and sign up online?',
        a: <>Yes. Each pickleball event gets a public registration page where players sign up online — choosing their skill division and event, selecting a partner for doubles, answering custom questions, accepting waivers and paying entry fees via Stripe.</>,
    },
    {
        q: 'Can ScorePal manage pickleball pool play?',
        a: <>Yes. ScorePal organizes pool play groups, fixtures and standings, then advances qualifiers into <Link to={"/group-and-knockout-generator/" as any} className="text-[#082F8A] underline font-semibold">championship or consolation brackets</Link>.</>,
    },
    {
        q: 'Can ScorePal support singles, doubles and mixed doubles?',
        a: <>Yes. ScorePal can support pickleball events for singles, doubles and mixed doubles divisions.</>,
    },
    {
        q: 'Can ScorePal manage skill divisions?',
        a: <>Yes. ScorePal can separate events by skill level, category or division — such as beginner, intermediate, advanced and open divisions.</>,
    },
    {
        q: 'Can ScorePal manage medal matches?',
        a: <>Yes. ScorePal helps organizers track final placements, including gold, silver, bronze and consolation matches.</>,
    },
    {
        q: 'Can ScorePal support pickleball leagues?',
        a: <>Yes. ScorePal also supports recurring pickleball leagues, ladder competitions, fixtures, standings and player rankings.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Pickleball Tournament Software for Pool Play, Brackets & Divisions | ScorePal</title>
            <meta name="description" content="Run pickleball tournaments with ScorePal. Manage pool play, skill divisions, singles, doubles, mixed doubles, consolation brackets, medal matches, scores and standings." />
            <link rel="canonical" href={`${config.publicUrl}/pickleball-tournament-software/`} />
            <meta property="og:title" content="Pickleball Tournament Software | ScorePal" />
            <meta property="og:description" content="Manage pickleball pool play, brackets, skill divisions, live scores and medal matches with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/pickleball-tournament-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/pickleball-tournament-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Pickleball Tournament Software | ScorePal" />
            <meta name="twitter:description" content="Run pickleball pool play, brackets, divisions and medal matches from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Pickleball Tournament Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Pickleball tournament software for managing pool play, brackets, divisions, live scores, standings and medal matches.",
                "url": "https://scorepal.co/pickleball-tournament-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is pickleball tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Pickleball tournament software helps organizers manage pool play, brackets, divisions, scores, standings, medal matches and player communication from one platform."}},
                    {"@type": "Question", "name": "Can players register and sign up online?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Each pickleball event gets a public registration page where players sign up online, choosing their skill division and event, selecting a partner for doubles, answering custom questions, accepting waivers and paying entry fees via Stripe."}},
                    {"@type": "Question", "name": "Can ScorePal manage pickleball pool play?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can help organize pool play groups, fixtures, standings and qualifiers into knockout or medal brackets."}},
                    {"@type": "Question", "name": "Can ScorePal support singles, doubles and mixed doubles?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can support pickleball events for singles, doubles and mixed doubles divisions."}},
                    {"@type": "Question", "name": "Can ScorePal manage skill divisions?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can help separate events by skill level, category or division, such as beginner, intermediate, advanced and open divisions."}},
                    {"@type": "Question", "name": "Can ScorePal manage medal matches?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can help organizers track final placements, including gold, silver, bronze and consolation matches."}},
                    {"@type": "Question", "name": "Can ScorePal support pickleball leagues?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can also support recurring pickleball leagues, ladder competitions, fixtures, standings and player rankings."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Pickleball Tournament Software", item: "https://scorepal.co/pickleball-tournament-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <DuprPartnerBadge />
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Pickleball Tournament Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Run pickleball tournaments with pool play, brackets and divisions
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps pickleball clubs and organizers manage pool play, championship brackets,
                                    consolation brackets, skill divisions, singles, doubles, mixed doubles, live scores and
                                    medal matches from one platform.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <a href="#pool-play" className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Explore Pickleball Formats
                                    </a>
                                </div>
                            </div>

                            {/* Event dashboard card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Pickleball event dashboard</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Keep pool standings, courts, brackets and medal matches clear throughout the event.</p>
                                <div className="space-y-3">
                                    {[
                                        {label: "3.5 Mixed Doubles", value: "Pool play active"},
                                        {label: "4.0 Men's Doubles", value: "Bracket ready"},
                                        {label: "Consolation Bracket", value: "Generated"},
                                        {label: "Medal Matches", value: "Next up"},
                                    ].map((row) => (
                                        <div key={row.label} className="flex items-center justify-between rounded-xl bg-white/10 px-4 py-3.5">
                                            <strong className="font-inter text-white text-sm">{row.label}</strong>
                                            <span className="font-inter text-white/70 text-sm">{row.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pool play + brackets */}
            <section id="pool-play" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <span className="inline-flex items-center rounded-full bg-[#EDEEF4] text-[#082F8A] px-3 py-1 text-xs font-bold mb-4">Pool Play + Brackets</span>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Manage pool play into championship brackets
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            Many pickleball tournaments start with pool play before moving into championship, consolation or
                            medal brackets. ScorePal helps organizers manage both stages without rebuilding spreadsheets after every result.
                        </p>
                        <div className="space-y-4">
                            {POOL_BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Pool standings mock */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            <div className="flex items-center justify-between gap-3 py-3 border-b border-[#eef0f4]">
                                <strong className="font-inter text-[#1a1a2e] text-sm">Pool A Standings</strong>
                                <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">Live</span>
                            </div>
                            {[
                                {p: "1. Team Parker", r: "3-0"},
                                {p: "2. Smash Squad", r: "2-1"},
                                {p: "3. Net Ninjas", r: "1-2"},
                                {p: "4. Dink Duo", r: "0-3"},
                            ].map((row, i, arr) => (
                                <div key={row.p} className={`flex items-center justify-between gap-3 py-3 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.p}</span>
                                    <strong className="text-[#1a1a2e]">{row.r}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Divisions */}
            <section id="divisions" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support pickleball skill divisions and event categories
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-3xl mb-10">
                        Pickleball tournaments often need separate divisions for skill level, age group, gender and event
                        type. Build each division clearly so players know exactly where they belong.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {DIVISIONS.map((d) => (
                            <div key={d.label} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <span className="inline-flex rounded-full bg-[#EDEEF4] text-[#082F8A] px-3 py-1 text-xs font-bold mb-3">{d.label}</span>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-base mb-2">{d.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{d.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything pickleball organizers need in one place
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-3xl mb-10">
                        Replace manual bracket files, paper scoresheets and messaging chaos with software built around the way pickleball tournaments actually run.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Formats */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Pickleball tournament formats ScorePal can support
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-3xl mb-10">
                        Choose the format that matches your event, from casual club play to competitive multi-division tournaments.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {FORMATS.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Operations + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Designed for real pickleball tournament operations
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            A pickleball event can quickly become complex when you have multiple divisions, doubles
                            categories, pool play, consolation rounds and medal matches happening at the same time.
                        </p>
                        <div className="space-y-4">
                            {OPS_BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Medal/bracket mock */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "4.0 Mixed Doubles", r: "Active", kind: "live"},
                                {l: "Championship Final", r: "Court 1"},
                                {l: "Bronze Medal Match", r: "Next", kind: "medal"},
                                {l: "Consolation Semi", r: "Court 3"},
                                {l: "Pool standings", r: "Completed"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.kind === "live"
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : row.kind === "medal"
                                            ? <span className="rounded-full bg-[#fffbeb] text-[#b54708] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                            : <strong className="text-[#1a1a2e]">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for pickleball tournaments
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-3xl mb-8">
                        Spreadsheets become difficult once you have pool standings, multiple divisions, bracket seeding, consolation matches and medal rounds to manage.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA card */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Need more than a pickleball bracket generator?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            Use free generators to plan your event, then manage the full tournament in ScorePal with pool play,
                            divisions, brackets, courts, medal matches, scores and standings in one place.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">Get started - it's free</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                View Free Tools
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular pickleball tools</h3>
                        <ul className="space-y-2 font-inter text-sm text-white/85">
                            <li><Link to={"/round-robin-tournament-generator/" as any} className="hover:underline">Round robin generator</Link></li>
                            <li><Link to={"/group-and-knockout-generator/" as any} className="hover:underline">Pool play and knockout generator</Link></li>
                            <li><Link to={"/single-elimination-bracket-generator/" as any} className="hover:underline">Single elimination bracket generator</Link></li>
                            <li><Link to={"/double-elimination-bracket-generator/" as any} className="hover:underline">Double elimination bracket generator</Link></li>
                            <li><Link to={"/ladder-league-generator/" as any} className="hover:underline">Ladder league generator</Link></li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next pickleball tournament with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Manage pool play, brackets, divisions, scores, standings and medal matches with pickleball tournament software built for modern organizers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Pool Play", "Skill Divisions", "Mixed Doubles", "Consolation Brackets", "Medal Matches", "Live Scores"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Pickleball Tournament Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
